import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { Observable, Subscription } from 'rxjs';
import { pluck } from 'rxjs/operators';
import { FlipType } from 'src/app/core/enums/FlipType';
import { Project, VersionInfo } from 'src/app/core/objects/project';
import { ColorsService } from 'src/app/core/services/colors.service';
import { NotificationsService } from 'src/app/core/services/notifications.service';
import { ProjectApiService } from 'src/app/core/services/project-api.service';
import { ProjectService } from 'src/app/core/services/project.service';
import { UserApiService } from 'src/app/core/services/user-api.service';
import { UserService } from 'src/app/core/services/user.service';
import { Group, Loads, LoadWithTranslatedName, StackingOptions, Load } from 'src/app/core/types';
import { validateZero } from 'src/app/core/validators/validators';
import { MaxLoadsPopupComponent } from '../../../../../../modal/components/maxLoadsPopup/max-loads-popup.component';
import { ModalService } from '../../../../../../modal/modal.service';
import { MessageService } from '../../../../../../core/services/message.service';
import { SharedChangesEditPopupComponent } from '../../../../../../container/shared-changes-edit-popup/shared-changes-edit-popup.component';
import {
  getFromLocalStorage,
  isMoreThanDaysAgo,
  setLocalStorage,
} from '../../../../../utils/localStorageHelpers';
import { projectDisplaysLayeredPallet } from '../../../../../../shared/helpers/pallet-loading-space.helper';
import { convertLoadDimensions } from '../../../../../../shared/helpers/unitRecalculationHelpers';
import { FormDataType } from '../../../../../add-load/add-load.component';

@Component({
    selector: 'gl-loads-list-element-edit',
    templateUrl: './loads-list-element-edit.component.html',
    styleUrls: ['./loads-list-element-edit.component.scss'],
    standalone: false
})
export class LoadsListElementEditComponent implements OnDestroy, OnInit {
  @Input() public group: Group;
  @Input() public workSpaceIndex: number;
  @Input() public stopIndex: number;
  @Input() public cargoSpaceIndex: number;
  @Input() public isOutsideSpace: boolean;
  @ViewChild('glBox', { static: false, read: ElementRef })
  private glBoxWindow: ElementRef<HTMLElement>;
  public form: FormModel;
  public saveLoadForm: SaveLoadForm;
  public loads: LoadWithTranslatedName[];
  @HostBinding('class.checkboxActive') public checkboxActive: boolean;
  public colorPicker = false;
  public stackingOptions: StackingOptions;
  @Output() private closeComponent = new EventEmitter<any>();
  private sortedLoads: Load[];
  private subscriptions: Subscription[] = [];
  private nameToSend: string;
  private cargoSpaceId: number;
  private currentWorkSpace: number;
  private project: Project;
  public previousTop = '0px';
  public currentType: number;
  public isPallet = false;
  public isSharedProjectLast = true;

  constructor(
    private projectService: ProjectService,
    private projectApiService: ProjectApiService,
    private userService: UserService,
    private userApiService: UserApiService,
    private colorsService: ColorsService,
    private translate: TranslateService,
    private renderer: Renderer2,
    private notificationsService: NotificationsService,
    private modalService: ModalService,
    private messageService: MessageService,
  ) {
    this.projectService.currentWorkSpaceIndex.subscribe(
      (currentWorkSpace: number) => (this.currentWorkSpace = currentWorkSpace),
    );
    this.projectService.project.subscribe((project: Project) => (this.project = project));
  }

  private displaySharedChangesEditPopup(): Observable<any> {
    return this.modalService.open(SharedChangesEditPopupComponent);
  }

  private getProjectVersions(): void {
    this.projectApiService.getProjectVersions().subscribe((versions: VersionInfo[]): void => {
      if (versions === null) return;
      const versionsLength: number = versions.length;
      const currentIndex: number = versions.findIndex((version: VersionInfo) => version.isCurrent);
      this.isSharedProjectLast = versionsLength - 1 === currentIndex;
    });
  }

  public loadArrangementChange(event: number): void {
    if (this.currentType === 2 && (event === 1 || event === 2)) {
      this.stackingOptions = { ...this.stackingOptions, formation: event, stacking: true };
    } else {
      this.stackingOptions = { ...this.stackingOptions, formation: event };
    }
  }

  public showAddToLoadingSpace(): boolean {
    return !projectDisplaysLayeredPallet(this.project);
  }

  public loadLoadsOntoSpace(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    const workSpaceId: number = this.project.workSpaces[this.workSpaceIndex].id;
    const stopId: number =
      this.stopIndex !== undefined
        ? this.project.workSpaces[this.workSpaceIndex].stops[this.stopIndex].id
        : undefined;
    const groupId: number = this.group.id;
    this.projectApiService.addFromOutside(workSpaceId, stopId, groupId).subscribe();
  }

  public ngOnInit(): void {
    this.getProjectVersions();
    this.isPallet = this.group.loadType === 3;
    this.userService.user.pipe(pluck('loads')).subscribe((loads: Loads): void => {
      loads?.user.forEach((load): void => {
        load.isUser = true;
      });
      this.sortedLoads = this.userService.sortLoadsByVolume(loads?.standard.concat(loads.user));
      this.loadLoads();
    });

    this.cargoSpaceId =
      this.cargoSpaceIndex >= 0
        ? this.project.workSpaces[this.workSpaceIndex].cargoSpaces[this.cargoSpaceIndex].id
        : undefined;

    this.form = this.generateLoadForm();
    this.subscriptions.push(
      this.form.controls.name.valueChanges.subscribe((value: string) => (this.nameToSend = value)),
    );
    this.subscriptions.push(
      this.form.controls.stacking.valueChanges.subscribe((): void => {
        this.stackingOptions.stacking = !this.form.value.stacking;
        this.recalculateTop();
      }),
    );
    this.saveLoadForm = new UntypedFormGroup({
      remember: new UntypedFormControl(false),
    });
    this.recalculateSumOfWeightsOnQuantityAndWeightChanges();
    this.recalculateWeightOnSumOfWeightsChanges();
    this.isSavedLoad(this.form.value.name);

    this.subscriptions.push(
      this.form.valueChanges.subscribe((value: FormModel['value']): void => {
        this.isSavedLoad(value.name);
      }),
    );
    this.stackingOptions = {
      stacking: this.group.stacking,
      maxLoadsOnTop: this.group.stackingOptions?.maxLoadsOnTop,
      maxWeightOnTop: this.group.stackingOptions?.maxWeightOnTop,
      boxesBelowAllowed: this.group.stackingOptions?.boxesBelowAllowed,
      alongFloor: this.group.stackingOptions?.alongFloor,
      pctSupport: this.group.stackingOptions?.pctSupport,
      formation: this.group.stackingOptions?.formation,
    };

    this.currentType = this.group.loadType;
  }

  public ngOnDestroy(): void {
    this.closeComponent.emit();
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  public recalculateTop(): void {
    if (this.glBoxWindow.nativeElement.style.top !== '0px') {
      this.previousTop = this.glBoxWindow.nativeElement.style.top;
      this.renderer.setStyle(this.glBoxWindow.nativeElement, 'top', '0px');
    } else {
      this.renderer.setStyle(this.glBoxWindow.nativeElement, 'top', this.previousTop);
    }
  }

  public isSavedLoad(name: string): void {
    const checkIsSavedLoad: LoadWithTranslatedName = this.loads?.find(
      (load: LoadWithTranslatedName): boolean => this.translate.instant(load.name) === name,
    );
    if (checkIsSavedLoad) {
      this.checkboxActive = false;
    } else {
      this.checkboxActive = true;
    }
  }

  public getGroupColorIcon(): string {
    return this.colorsService.colorIcons.get(this.group.color);
  }

  public closeEditWindow() {
    this.closeComponent.emit();
  }

  public generateStackInfo() {
    const message = this.translate.instant('stack_quantity_info_group') as string;
    return message + ' ' + this.group.stackingOptions.maxLoadsOnTop + ')';
  }

  public openColorPicker(): void {
    const localStorageData: { data: string; date: string } = getFromLocalStorage('editLoads');
    const isPassedDays: boolean = localStorageData
      ? isMoreThanDaysAgo(new Date(localStorageData.date), 1)
      : false;
    if (isPassedDays) {
      localStorage.removeItem('editLoads');
    }
    const isPopupTouched: boolean = localStorageData ? localStorageData.data === 'true' : false;
    if (!this.isSharedProjectLast && !isPopupTouched) {
      this.displaySharedChangesEditPopup().subscribe((result): void => {
        setLocalStorage('true', 'editLoads');
        if (result.data === 'yes') {
          this.colorPicker = true;
        } else {
          return;
        }
      });
    } else {
      this.colorPicker = true;
    }
  }

  public closeColorPicker(): void {
    this.colorPicker = false;
  }

  public rotate(): void {
    const localStorageData: { data: string; date: string } = getFromLocalStorage('editLoads');
    const isPassedDays: boolean = localStorageData
      ? isMoreThanDaysAgo(new Date(localStorageData.date), 1)
      : false;
    if (isPassedDays) {
      localStorage.removeItem('editLoads');
    }
    const isPopupTouched: boolean = localStorageData ? localStorageData.data === 'true' : false;
    if (!this.isSharedProjectLast && !isPopupTouched) {
      this.displaySharedChangesEditPopup().subscribe((result): void => {
        setLocalStorage('true', 'editLoads');
        if (result.data === 'yes') {
          this.rotateLoad();
        } else {
          return;
        }
      });
    } else {
      this.rotateLoad();
    }
  }

  private rotateLoad(): void {
    this.projectApiService
      .editGroup({
        currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
        target: {
          workSpaceId: this.project.workSpaces[this.workSpaceIndex].id,
          stopId:
            this.stopIndex >= 0
              ? this.project.workSpaces[this.workSpaceIndex].stops[this.stopIndex].id
              : undefined,
          cargoSpaceId: this.cargoSpaceId,
          groupId: this.group.id,
        },
        quantityDifference: 0,
        load: {
          reverted: true,
        },
      })
      .subscribe(() => this.closeComponent.emit());
  }

  public delete(): void {
    const localStorageData: { data: string; date: string } = getFromLocalStorage('editLoads');
    const isPassedDays: boolean = localStorageData
      ? isMoreThanDaysAgo(new Date(localStorageData.date), 1)
      : false;
    if (isPassedDays) {
      localStorage.removeItem('editLoads');
    }
    const isPopupTouched: boolean = localStorageData ? localStorageData.data === 'true' : false;
    if (!this.isSharedProjectLast && !isPopupTouched) {
      this.displaySharedChangesEditPopup().subscribe((result): void => {
        setLocalStorage('true', 'editLoads');
        if (result.data === 'yes') {
          this.deleteLoad();
        } else {
          return;
        }
      });
    } else {
      this.deleteLoad();
    }
  }

  private deleteLoad(): void {
    this.projectApiService
      .deleteGroup({
        currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
        target: {
          workSpaceId: this.project.workSpaces[this.workSpaceIndex].id,
          stopId:
            this.stopIndex >= 0
              ? this.project.workSpaces[this.workSpaceIndex].stops[this.stopIndex].id
              : undefined,
          cargoSpaceId: this.cargoSpaceId,
          groupId: this.group.id,
        },
      })
      .subscribe();
  }

  public flip(): void {
    const localStorageData: { data: string; date: string } = getFromLocalStorage('editLoads');
    const isPassedDays: boolean = localStorageData
      ? isMoreThanDaysAgo(new Date(localStorageData.date), 1)
      : false;
    if (isPassedDays) {
      localStorage.removeItem('editLoads');
    }
    const isPopupTouched: boolean = localStorageData ? localStorageData.data === 'true' : false;
    if (!this.isSharedProjectLast && !isPopupTouched) {
      this.displaySharedChangesEditPopup().subscribe((result): void => {
        setLocalStorage('true', 'editLoads');
        if (result.data === 'yes') {
          this.flipLoad();
        } else {
          return;
        }
      });
    } else {
      this.flipLoad();
    }
  }

  private flipLoad(): void {
    this.projectApiService
      .flipGroup({
        currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
        target: {
          workSpaceId: this.project.workSpaces[this.workSpaceIndex].id,
          stopId:
            this.stopIndex >= 0
              ? this.project.workSpaces[this.workSpaceIndex].stops[this.stopIndex].id
              : undefined,
          cargoSpaceId: this.cargoSpaceId,
          groupId: this.group.id,
        },
        flipType: FlipType.Unimportant,
      })
      .subscribe(() => this.closeComponent.emit());
  }

  public flipShorter(): void {
    const localStorageData: { data: string; date: string } = getFromLocalStorage('editLoads');
    const isPassedDays: boolean = localStorageData
      ? isMoreThanDaysAgo(new Date(localStorageData.date), 1)
      : false;
    if (isPassedDays) {
      localStorage.removeItem('editLoads');
    }
    const isPopupTouched: boolean = localStorageData ? localStorageData.data === 'true' : false;
    if (!this.isSharedProjectLast && !isPopupTouched) {
      this.displaySharedChangesEditPopup().subscribe((result): void => {
        setLocalStorage('true', 'editLoads');
        if (result.data === 'yes') {
          this.flipLoadShorter();
        } else {
          return;
        }
      });
    } else {
      this.flipLoadShorter();
    }
  }

  private flipLoadShorter(): void {
    this.projectApiService
      .flipGroup({
        currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
        target: {
          workSpaceId: this.project.workSpaces[this.workSpaceIndex].id,
          stopId:
            this.stopIndex >= 0
              ? this.project.workSpaces[this.workSpaceIndex].stops[this.stopIndex].id
              : undefined,
          cargoSpaceId: this.cargoSpaceId,
          groupId: this.group.id,
        },
        flipType: FlipType.Shorter,
      })
      .subscribe(() => this.closeComponent.emit());
  }

  public flipLonger(): void {
    const localStorageData: { data: string; date: string } = getFromLocalStorage('editLoads');
    const isPassedDays: boolean = localStorageData
      ? isMoreThanDaysAgo(new Date(localStorageData.date), 1)
      : false;
    if (isPassedDays) {
      localStorage.removeItem('editLoads');
    }
    const isPopupTouched: boolean = localStorageData ? localStorageData.data === 'true' : false;
    if (!this.isSharedProjectLast && !isPopupTouched) {
      this.displaySharedChangesEditPopup().subscribe((result): void => {
        setLocalStorage('true', 'editLoads');
        if (result.data === 'yes') {
          this.flipLoadLonger();
        } else {
          return;
        }
      });
    } else {
      this.flipLoadLonger();
    }
  }

  private flipLoadLonger(): void {
    this.projectApiService
      .flipGroup({
        currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
        target: {
          workSpaceId: this.project.workSpaces[this.workSpaceIndex].id,
          stopId:
            this.stopIndex >= 0
              ? this.project.workSpaces[this.workSpaceIndex].stops[this.stopIndex].id
              : undefined,
          cargoSpaceId: this.cargoSpaceId,
          groupId: this.group.id,
        },
        flipType: FlipType.Longer,
      })
      .subscribe(() => this.closeComponent.emit());
  }

  public changeColor(color: number): void {
    this.projectApiService
      .editGroup({
        currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
        target: {
          workSpaceId: this.project.workSpaces[this.workSpaceIndex].id,
          groupId: this.group.id,
        },
        quantityDifference: 0,
        load: {
          color,
        },
      })
      .subscribe();
  }

  public onSubmit(): void {
    const isLoadLimitReached: boolean =
      this.projectService.currentNumberOfLoads +
        parseFloat(this.form.value.quantity as unknown as string) -
        this.group.quantity >
      this.projectService.maxNumberOfLoads;
    if (isLoadLimitReached) {
      this.modalService.open(MaxLoadsPopupComponent).subscribe();
      return;
    }
    const localStorageData: { data: string; date: string } = getFromLocalStorage('editLoads');
    const isPassedDays: boolean = localStorageData
      ? isMoreThanDaysAgo(new Date(localStorageData.date), 1)
      : false;
    if (isPassedDays) {
      localStorage.removeItem('editLoads');
    }
    const isPopupTouched: boolean = localStorageData ? localStorageData.data === 'true' : false;
    if (!this.isSharedProjectLast && !isPopupTouched) {
      this.displaySharedChangesEditPopup().subscribe((result): void => {
        setLocalStorage('true', 'editLoads');
        if (result.data === 'yes') {
          this.editLoadOnSubmit();
        } else {
          return;
        }
      });
    } else {
      this.editLoadOnSubmit();
    }
  }

  private editLoadOnSubmit(): void {
    if (this.isFormValid()) {
      const isMm: boolean = this.project.options.unit === 'mm';
      const loadConverted: Partial<Load> = isMm
        ? convertLoadDimensions(
            this.generateLoadObjectOnlyWithChangedValues() as FormDataType,
            this.project.options.unit,
          )
        : this.generateLoadObjectOnlyWithChangedValues();
      const editLoad = {
        currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
        target: {
          workSpaceId: this.project.workSpaces[this.workSpaceIndex].id,
          stopId:
            this.stopIndex >= 0
              ? this.project.workSpaces[this.workSpaceIndex].stops[this.stopIndex].id
              : undefined,
          cargoSpaceId: this.cargoSpaceId,
          groupId: this.group.id,
        },
        quantityDifference: this.form.value.quantity - this.group.quantity,
        load: loadConverted,
      };
      this.projectApiService.editGroup(editLoad).subscribe((result): void => {
        if (this.saveLoadForm.value.remember && this.checkboxActive) {
          const saveLoad: Partial<Load> = {
            name: this.nameToSend ? this.nameToSend : 'load_without_name',
            length: this.form.value.length,
            width:
              this.currentType === 1 || this.currentType === 2
                ? this.form.value.diameter
                : this.form.value.width,
            height: this.form.value.height,
            weight: this.form.value.weight ? this.form.value.weight : 0,
            stacking: this.form.value.stacking ? this.stackingOptions.stacking : false,
            diameter: this.form.value.diameter,
            loadType: this.group.loadType,
            stackingOptions: this.stackingOptions,
            rotateOnBase: this.group.rotateOnBase === null ? undefined : this.group.rotateOnBase,
            rotateFreely: this.group.rotateFreely === null ? undefined : this.group.rotateFreely,
            allowToRotate: this.group.allowToRotate === null ? undefined : this.group.allowToRotate,
          };
          let convertedLoad: Partial<Load> = saveLoad;
          if (isMm) {
            convertedLoad = convertLoadDimensions(
              saveLoad as FormDataType,
              this.project.options.unit,
            );
          }
          this.userApiService.saveLoad(convertedLoad).subscribe();
        }
        if (result) {
          this.notificationsService.generateCOGNotification(result);
          this.notificationsService.generateLoadNotDisplayedNotification(editLoad.load, result);
        }
      });
      this.closeComponent.emit();
    }
  }

  private isFormValid(): number {
    if (this.currentType === 0) {
      return (
        this.form.value.length &&
        this.form.value.width &&
        this.form.value.height &&
        this.form.value.quantity
      );
    } else if (this.currentType === 1 || this.currentType === 2) {
      return this.form.value.height && this.form.value.diameter && this.form.value.quantity;
    } else {
      return (
        this.form.value.length &&
        this.form.value.width &&
        this.form.value.height &&
        this.form.value.quantity
      );
    }
  }

  public areGroupDimensionsSame() {
    return this.group.dimensions.width === this.group.dimensions.length;
  }

  public setStackingOptions(options: StackingOptions): void {
    this.messageService.sendMessage({ name: 'stackingChange', data: options.stacking });
    this.stackingOptions = { ...options };
    this.form.get('stacking').setValue(options.stacking, { emitEvent: false });
  }

  private loadLoads(): void {
    this.loads = this.sortedLoads?.map((load: Load) => {
      return {
        ...load,
        translatedName: this.translateLoadName(load.name),
      };
    });
  }

  private translateLoadName(loadName: string): string {
    if (loadName) {
      return this.translate.instant(loadName) as string;
    } else {
      return loadName;
    }
  }

  private generateLoadObjectOnlyWithChangedValues(): Partial<Load> {
    const load: Partial<Load> = {};
    if (
      this.group.dimensions.length !== this.form.value.length ||
      this.group.dimensions.width !== this.form.value.width ||
      this.group.dimensions.height !== this.form.value.height
    ) {
      load.length = this.form.value.length;
      load.width = this.form.value.width;
      load.height = this.form.value.height;
    }
    if (this.group.weight !== this.form.value.weight) {
      load.weight = this.form.value.weight;
    }

    if (this.group.dimensions.diameter !== this.form.value.diameter) {
      load.diameter = this.form.value.diameter;
    }

    if (this.group.name !== this.nameToSend) {
      load.name = this.nameToSend;
    }
    let stackingOptions: StackingOptions = null;
    if (this.stackingOptions.stacking !== this.group.stacking) {
      load.stacking = this.stackingOptions.stacking;
    }

    if (this.group.stackingOptions.alongFloor !== this.stackingOptions.alongFloor) {
      stackingOptions = {
        ...stackingOptions,
        alongFloor: this.stackingOptions.alongFloor,
      };
    }

    if (
      !_.isEqual(
        this.group.stackingOptions.boxesBelowAllowed,
        this.stackingOptions.boxesBelowAllowed,
      )
    ) {
      stackingOptions = {
        ...stackingOptions,
        boxesBelowAllowed: this.stackingOptions.boxesBelowAllowed,
      };
    }
    const maxLoadsOnTopGroup: number | string =
      this.group.stackingOptions.maxLoadsOnTop === undefined
        ? null
        : this.group.stackingOptions.maxLoadsOnTop;
    const maxLoadsOnTop: number | string =
      this.stackingOptions.maxLoadsOnTop === undefined ? null : this.stackingOptions.maxLoadsOnTop;
    if (maxLoadsOnTopGroup !== maxLoadsOnTop) {
      stackingOptions = {
        ...stackingOptions,
        maxLoadsOnTop: this.stackingOptions.maxLoadsOnTop,
      };
    }
    if (this.group.stackingOptions.maxWeightOnTop !== this.stackingOptions.maxWeightOnTop) {
      stackingOptions = {
        ...stackingOptions,
        maxWeightOnTop:
          this.stackingOptions.maxWeightOnTop === 0 ? null : this.stackingOptions.maxWeightOnTop,
      };
    }

    if (this.group.stackingOptions.pctSupport !== this.stackingOptions.pctSupport) {
      stackingOptions = {
        ...stackingOptions,
        pctSupport: this.stackingOptions.pctSupport,
      };
    }
    let formation: number =
      this.group.loadType === 2 &&
      this.stackingOptions.stacking &&
      this.stackingOptions.formation === undefined
        ? 2
        : this.stackingOptions.formation;
    formation = this.group.loadType === 1 ? this.stackingOptions.formation : formation;
    if (
      (this.group.loadType === 1 || this.group.loadType === 2) &&
      this.group.stackingOptions.formation !== this.stackingOptions.formation
    ) {
      stackingOptions = {
        ...stackingOptions,
        formation,
      };
    } else if (this.group.loadType === 1 || this.group.loadType === 2) {
      stackingOptions = {
        ...stackingOptions,
        formation,
      };
    }

    if (stackingOptions !== null) {
      load.stackingOptions = stackingOptions;
    }

    if (!this.stackingOptions.stacking) {
      const stacking: boolean = this.stackingOptions.stacking;
      this.stackingOptions = {
        maxLoadsOnTop: null,
        maxWeightOnTop: null,
        boxesBelowAllowed: null,
        alongFloor: false,
      };

      if (this.group.loadType === 1) {
        this.stackingOptions.formation = formation;
      }
      if (this.group.loadType === 2) {
        this.stackingOptions.formation = 0;
      }
      if (stacking !== this.group.stacking) {
        load.stackingOptions = this.stackingOptions;
      }
    }
    return load;
  }

  private recalculateWeightOnSumOfWeightsChanges(): void {
    this.subscriptions.push(
      this.form.controls.sumOfWeights.valueChanges.subscribe((sumOfWeights: number): void => {
        if (sumOfWeights !== null && this.isQuantityAndWeightAndSumOfWeightsValid()) {
          this.form.controls.weight.setValue(
            Math.floor((sumOfWeights / this.form.value.quantity) * 10) / 10,
            { emitEvent: false },
          );
        }
      }),
    );
  }

  private recalculateSumOfWeightsOnQuantityAndWeightChanges(): void {
    this.subscriptions.push(
      this.form.controls.quantity.valueChanges.subscribe((quantity: number): void => {
        if (quantity !== null && this.isQuantityAndWeightAndSumOfWeightsValid()) {
          this.form.controls.sumOfWeights.setValue(
            Math.floor(this.form.value.weight * quantity * 10) / 10,
            { emitEvent: false },
          );
        }
      }),
      this.form.controls.weight.valueChanges.subscribe((weight: number): void => {
        if (weight !== null && this.isQuantityAndWeightAndSumOfWeightsValid()) {
          this.form.controls.sumOfWeights.setValue(
            Math.floor(this.form.value.quantity * weight * 10) / 10,
            { emitEvent: false },
          );
        }
      }),
    );
  }

  private isQuantityAndWeightAndSumOfWeightsValid(): boolean {
    return this.form.controls.quantity.valid && this.form.controls.weight.valid;
  }

  private generateLoadForm(): UntypedFormGroup {
    this.nameToSend = this.translate.instant(this.group.name) as string;
    return new UntypedFormGroup({
      name: new UntypedFormControl(this.translate.instant(this.group.name), [
        Validators.required,
        Validators.maxLength(255),
      ]),
      length: new UntypedFormControl(this.group.dimensions.length, [Validators.required, validateZero]),
      width: new UntypedFormControl(this.group.dimensions.width, [Validators.required, validateZero]),
      height: new UntypedFormControl(this.group.dimensions.height, [Validators.required, validateZero]),
      weight: new UntypedFormControl(this.group.weight, []),
      quantity: new UntypedFormControl(this.group.quantity, [Validators.required, validateZero]),
      stacking: new UntypedFormControl(this.group.stacking),
      sumOfWeights: new UntypedFormControl(_.round(this.group.quantity * this.group.weight, 2), []),
      diameter: new UntypedFormControl(this.group.dimensions.diameter, [
        Validators.required,
        validateZero,
      ]),
    });
  }
}

interface FormModel extends UntypedFormGroup {
  value: {
    name: string;
    length: number;
    width: number;
    height: number;
    weight: number;
    quantity: number;
    stacking: boolean;
    sumOfWeights: number;
    diameter: number;
  };
}

interface SaveLoadForm extends UntypedFormGroup {
  value: {
    remember: boolean;
  };
}
